<template>
    <app-layout title="Settings">
        <page-header>
            <template #title>Settings - Flyers</template>

            <template #actions>
                <jet-tertiary-button type="button" class="h-7 leading-none" @click="downloadCurrentFlyer()">
                    Download Current Flyer
                </jet-tertiary-button>
                
                <jet-button type="button" class="h-7 leading-none" @click="addNewFlyer()" >
                    Add Flyer
                </jet-button>
            </template>

        </page-header>

        <div class="overflow-y-auto" :style="getMaxHeight()">
            <settings-navigation />

            <div class="px-8 mb-4">
                <div v-for="flyer, index in flyerList" :key="flyer.id" class="flex flex-col rounded border p-4 mb-4">
                    
                    <div class="flex">
                        <div class="flex w-full">
                            <div class="flex flex-col w-2/3 items-center">
                                <font-awesome-icon
                                    v-if="!flyer.filepath"
                                    :icon="['fa', 'file-pdf']"
                                    class="h-16 w-16 p-8 m-4 border rounded cursor-pointer"
                                    @click="openFileInput(index)"
                                />
                                <input hidden type="file" ref="fileInput" accept="pdf/*" @change="inputFile($event, index)"/>
                                <iframe v-if="flyer.filepath" :src="/storage/ + flyer.filepath"  width="100%" height="500px" frameborder="0"></iframe>
                                <p v-if="flyer.file">{{flyer.file.name}}</p>                                
                                <jet-input-error class="mt-1" :message="flyer.errors.filepath ? flyer.errors.filepath[0] : null" />
                            </div>
                            <div class="w-1/3 ml-4 mb-5">
                                <div class="mb-2">
                                    <jet-label value="Title" />
                                    <jet-input type="text" class="w-full" v-model="flyer.title" @change="changesMade(index)"/>
                                    <jet-input-error class="mt-1" :message="flyer.errors.title ? flyer.errors.title[0] : null" />
                                </div>

                                <div class="mb-2">
                                    <jet-label value="Start" />
                                    <jet-input type="date" class="w-full" v-model="flyer.start_time" :max="flyer.end_time" @change="changesMade(index)"/>
                                    <jet-input-error class="mt-1" :message="flyer.errors.start_time ? flyer.errors.start_time[0] : null" />
                                </div>

                                <div class="mb-2">
                                    <jet-label value="End" />
                                    <jet-input type="date" class="w-full" v-model="flyer.end_time" :min="flyer.start_time" @change="changesMade(index)"/>
                                    <div class="text-xs text-gray-500">Leave blank for no expiry.</div>
                                    <jet-input-error class="mt-1" :message="flyer.errors.end_time ? flyer.errors.end_time[0] : null" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex justify-end">
                        <template v-if="flyer.changed">
                            <jet-button :disabled="flyer.errors.target_url" class="mr-2" @click="handleSaveFlyer(index)">
                                Save
                            </jet-button>
                            <jet-tertiary-button v-if="flyer.id" class="ml-2 mr-2" @click="handleCancelFlyerChanges(flyer.id, index)">
                                Cancel
                            </jet-tertiary-button>
                        </template>
                        <jet-danger-button class="ml-2" @click="deleteFlyer(flyer.id, index)">
                            Delete Flyer
                        </jet-danger-button>
                    </div>
                </div>

                <div ref="end" />

                <!-- Delete flyer modal -->
                <jet-confirmation-modal :show="deletedFlyerId" @close="deletedFlyerId = false">
                    <template #title>
                        Delete Flyer
                    </template>

                    <template #content>
                        Are you sure you would like to delete?
                    </template>

                    <template #footer>
                        <jet-danger-button @click="handleDeleteFlyer">
                            Yes
                        </jet-danger-button>

                        <jet-tertiary-button class="ml-2" @click="deletedFlyerId = false">
                            No
                        </jet-tertiary-button>
                    </template>
                </jet-confirmation-modal>

            </div>
        </div>
    </app-layout>
</template>

<script>
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Layouts/partials/PageHeader';
import SettingsNavigation from '@/Pages/Settings/Partials/SettingsNavigation';
import JetInput from '@/Jetstream/Input';
import JetLabel from '@/Jetstream/Label';
import JetInputError from '@/Jetstream/InputError';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

export default {
    components: {
        AppLayout,
        PageHeader,
        SettingsNavigation,
        JetInput,
        JetButton,
        JetInputError,
        JetDangerButton,
        JetTertiaryButton,
        JetConfirmationModal,
        QuillEditor,
        JetLabel
    },

    props: ['flyers'],

    data() {
        return {
            flyerList: [],
            deletedFlyerId: false,
            deletedPictureIndex: false,
        }
    },

    mounted() {
        this.setFlyers();
    },

    methods: {
        getMaxHeight() {
            // page-header sizes
            let marginB = '1.5rem';
            let paddingY = '0.75rem';
            let lineHeight = '2.25rem';
            let borderB = '1px';

            return `max-height: calc(100vh - 2 * ${paddingY} - ${lineHeight} - ${borderB} - ${marginB})`;
        },

        setFlyers() {
            this.flyers.forEach(flyer => {
                this.flyerList.push({
                    id: flyer.id,
                    changed: false,
                    filepath:flyer.filepath,
                    start_time: flyer.start_time,
                    end_time:flyer.end_time,
                    title: flyer.title,
                    errors: {
                        file: null,
                    },
                })
            });
        },

        addNewFlyer() {
            this.flyerList.push({
                title: '',
                filepath: '',
                changed: true,
                start_time: null,
                end_time:null,
                errors: {
                    file: null,
                },
            });

            this.$nextTick(() => {
                this.$refs.end.scrollIntoView({ behavior: 'smooth' });
            });
        },

        openFileInput(index) {
            this.$refs.fileInput[index].click();
        },

        inputFile(event, index) {
            let file = event.target.files[0];

            if (file.type === "application/pdf") {
                this.flyerList[index].errors.file = null;
                event.target.value = null;
                this.flyerList[index].file = file;
                this.changesMade(index);
            } else {
                this.flyerList[index].errors.file = "The file must be a file of type: pdf";
            }
        },

        changesMade(index) {
            this.flyerList[index].changed = true;
        },

        removeFlyer(index) {
            this.flyerList.splice(index, 1);
        },

        deleteFlyer(id, index) {
            if (!id) {
                this.removeFlyer(index);
            } else {
                this.deletedFlyerId = id;
            }
        },

        handleCancelFlyerChanges(id, index) {
            axios.get(route('settings.flyers.show', id)).then(({data}) => {
                let item = this.flyerList[index];
                item.changed = false;
                item.filepath = data.flyer.filepath;
                item.flyer = data.flyer.flyer;
                item.image = null;
                item.errors.file = null;
                item.start_time = data.flyer.start_time;
                item.end_time = data.flyer.end_time;
            });
        },

        handleSaveFlyer(index) {
            let item = this.flyerList[index];

            const formData = new FormData();
            if (item.title) formData.append('title', item.title ?? null);
            if (item.start_time) formData.append('start_time', item.start_time);
            if (item.end_time) formData.append('end_time', item.end_time);

            if (item.file) formData.append('file', item.file);
            if (item.file) formData.append('filepath', item.file);
            else if (item.filepath) formData.append('filepath', item.filepath);

            let url = item.id ? route('settings.flyers.update', item.id) : route('settings.flyers.store');

            axios.post(url, formData)
            .then(({data}) => {
                item.id = data.flyer.id;
                item.title = data.flyer.title;
                item.changed = false;
                item.file = null;
                item.filepath = data.flyer.filepath;
                item.errors.file = null;
                item.start_time = data.flyer.start_time;
                item.end_time = data.flyer.end_time;

                if (data.flyer.filepath) {
                    item.filepath = data.flyer.filepath;
                }
            })
            .catch((error) => {
                item.errors = error.response.data.errors;
            })
        },

        handleDeleteFlyer() {
            axios.delete(route('settings.flyers.destroy', this.deletedFlyerId))
                .then(() => {
                    this.removeFlyer(this.flyerList.findIndex(item => item.id === this.deletedFlyerId));
                    this.deletedFlyerId = false;
                })
        },

        checkValid(value, index) {
            this.changesMade(index);

            if (value.length > 0) {
                this.flyerList[index].errors.target_url = null;
            }
        },

        downloadCurrentFlyer() {
            location.href = route('settings.flyers.download-current')
        },
    },
}
</script>

<style scoped>

</style>
